"use client";

import { api } from "@/api";
import classNames from "classnames";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "vidmuse:announcement-bar:bonus-251224";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const AnnouncementBar: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [showPromotion, setShowPromotion] = useState(false);
  const timerRef = useRef<number | null>(null);

  // 清理定时器的辅助函数
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 计算倒计时
  const calculateTimeLeft = useCallback(
    (deadline: number) => {
      const now = Date.now();
      const difference = deadline - now;

      if (difference <= 0) {
        clearTimer();
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    },
    [clearTimer]
  );

  const fetchPromotionData = useCallback(async () => {
    try {
      const { data } = await api.promotions.getActivePromotions();
      if (!data?.creditPackagePromotion) {
        setShowPromotion(false);
        return;
      }

      const { startTime, endTime } = data.creditPackagePromotion;
      if (!startTime || !endTime) {
        setShowPromotion(false);
        return;
      }

      const now = new Date();
      const start = new Date(startTime);
      const end = new Date(endTime);

      const isInRange = now >= start && now <= end;

      setShowPromotion(isInRange);

      if (isInRange) {
        const endTimeMs = end.getTime();

        // 清理现有定时器
        clearTimer();

        setTimeLeft(calculateTimeLeft(endTimeMs));

        // 每秒更新
        timerRef.current = window.setInterval(() => {
          setTimeLeft(calculateTimeLeft(endTimeMs));
        }, 1000);
      } else {
        // 如果不在促销范围内，确保清理定时器
        clearTimer();
      }
    } catch (error) {
      console.error("Failed to fetch promotion data:", error);
    }
  }, [calculateTimeLeft, clearTimer]);

  // 获取促销信息
  useEffect(() => {
    // 检查是否已关闭
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed === "true") {
      setShowPromotion(false);
      return;
    }

    fetchPromotionData();
  }, [fetchPromotionData]);

  // 处理促销过期
  useEffect(() => {
    if (showPromotion && timeLeft === null) {
      setShowPromotion(false);
    }
  }, [showPromotion, timeLeft]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    clearTimer();
    localStorage.setItem(STORAGE_KEY, "true");
    setShowPromotion(false);
  };

  if (!showPromotion) {
    return null;
  }

  return (
    <div
      className="w-full"
      style={{
        background: "linear-gradient(90deg, #9466FF -0.66%, #ECE1FF 100%)",
      }}
    >
      <Link
        className="text-[#3D237B]!"
        href="/pricing"
        data-disable-progress={true}
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-center py-2 pr-10 sm:pr-12">
            <div className="flex flex-row items-center gap-1.5 sm:gap-3 text-center max-w-full">
              <span className="text-xs sm:text-base font-normal leading-tight truncate">
                <span className="mr-2">🎄</span>
                <span className="font-semibold">
                  Holiday & New Year Special:
                </span>{" "}
                Get up to{" "}
                <span className="font-bold">5,000 First Bonus Credits</span> &{" "}
                <span className="font-bold">+50% FREE on all Top-ups!</span>
              </span>

              {timeLeft && (
                <div className="flex items-center gap-1 text-xs sm:text-sm font-bold shrink-0 text-white">
                  <span className="bg-[#9466FF] px-1.5 py-0.5 rounded">
                    {String(timeLeft.days).padStart(2, "0")}d
                  </span>
                  <span className="text-[#9466FF]">:</span>
                  <span className="bg-[#9466FF] px-1.5 py-0.5 rounded">
                    {String(timeLeft.hours).padStart(2, "0")}h
                  </span>
                  <span className="text-[#9466FF]">:</span>
                  <span className="bg-[#9466FF] px-1.5 py-0.5 rounded">
                    {String(timeLeft.minutes).padStart(2, "0")}m
                  </span>
                  <span className="text-[#9466FF]">:</span>
                  <span className="bg-[#9466FF] px-1.5 py-0.5 rounded">
                    {String(timeLeft.seconds).padStart(2, "0")}s
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={handleDismiss}
              className={classNames(
                "absolute right-2 sm:right-4 top-1/2 -translate-y-1/2",
                "p-1 rounded-full hover:bg-black/10 transition-colors",
                "text-black"
              )}
              aria-label="Close announcement"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
};
